import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/Button';
import {
  hasFirebaseSession,
  refreshEmailVerified,
  sendSignupVerificationEmail,
  subscribeToFirebaseSession,
} from '../../services/auth';
import { SignupError, UNKNOWN_MESSAGE, createSignup } from '../../services/signup';
import type { SignupSport } from '../../types/signup';
import { SignupHeader } from './SignupLayout';
import type { SignupOutcomeState } from './SignupOutcome';
import { clearSignup } from './signupStorage';
import { useCooldown } from './useCooldown';

/** Seconds between verification emails. Firebase throttles harder than this on its own. */
export const RESEND_COOLDOWN_SECONDS = 60;

const notVerifiedMessage = (email: string) =>
  `Your email isn't verified yet. Open the link we sent to ${email}, then try again.`;

/**
 * Third step: wait for the coach to click the verification link.
 *
 * **The RosterPilot program is only created once Firebase says the address is verified.** "I've
 * verified" reloads the Firebase user and force-refreshes the ID token first, because the API reads
 * `email_verified` from the token, and a token minted before the click still says false.
 */
export const SignupVerifyStep = ({
  email,
  firstName,
  lastName,
  sport,
  alreadyVerified,
  verificationSendFailed,
  onOutcome,
  onSignInAgain,
  onStartOver,
}: {
  email: string;
  firstName: string;
  lastName: string;
  sport: SignupSport;
  alreadyVerified: boolean;
  verificationSendFailed: boolean;
  onOutcome: (outcome: SignupOutcomeState) => void;
  /** Back to the account step, form filled in, to sign in to this account again. */
  onSignInAgain: () => void;
  onStartOver: () => void;
}) => {
  const { refreshProfile, logout } = useAuth();
  /**
   * Firebase's own signed-in state, not AuthContext's user: that one waits on a profile request, so
   * it cannot tell "still loading" from "signed out in another tab". Everything on this step needs
   * the session, so losing it gets its own screen rather than a generic check error.
   */
  const [signedIn, setSignedIn] = useState(hasFirebaseSession);
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(
    verificationSendFailed
      ? 'We could not send the verification email. Use Resend email to try again.'
      : ''
  );
  const [notice, setNotice] = useState('');
  const cooldown = useCooldown(verificationSendFailed ? 0 : RESEND_COOLDOWN_SECONDS);
  const inFlightRef = useRef(false);

  useEffect(() => subscribeToFirebaseSession(setSignedIn), []);

  /**
   * A refusal that ends signup here. The Firebase account has no program and cannot become one, and
   * on these outcomes the server deletes it (so the coach can later accept an invitation for the
   * same address). The local session is signed out through AuthContext, which also clears the
   * stored form and per-user caches, so nothing goes on using a session whose account is gone.
   * Signing out needs no server call, so it succeeds even when the account no longer exists; a
   * failure is swallowed rather than shown, since the outcome screen is already the answer.
   */
  const endWith = useCallback(
    (outcome: SignupOutcomeState) => {
      clearSignup();
      onOutcome(outcome);
      void logout().catch(() => {
        // The next account creation replaces a stale session, and /signup signs out any session it
        // cannot resume.
      });
    },
    [onOutcome, logout]
  );

  const createProgram = useCallback(async () => {
    try {
      await createSignup({ firstName, lastName, sport });
    } catch (createError: unknown) {
      if (!(createError instanceof SignupError)) {
        setError(UNKNOWN_MESSAGE);
        return;
      }

      switch (createError.code) {
        case 'email_not_verified':
          setError(notVerifiedMessage(email));
          return;
        case 'already_exists':
          // The lookup never reveals a taken program, so this is where the coach learns of it.
          endWith({ phase: 'alreadyExists', email });
          return;
        case 'email_in_use':
        case 'already_registered':
          endWith({ phase: 'signInInstead', email, message: createError.message });
          return;
        case 'unverified_domain':
          endWith({ phase: 'unverified' });
          return;
        case 'unavailable':
          endWith({ phase: 'unavailable' });
          return;
        case 'unauthorized':
          // The token no longer names a signed-in account (signed out elsewhere, or expired).
          if (!hasFirebaseSession()) {
            setSignedIn(false);
            return;
          }
          setError(createError.message);
          return;
        default:
          // invalid_*, rate limits and server errors: say what the server said and leave Start over
          // within reach.
          setError(createError.message);
          return;
      }
    }

    // The program exists and awaits payment. Re-read the profile so the app treats this person as
    // a signed-in admin (whose organization is awaiting payment) rather than a Firebase-only user.
    try {
      await refreshProfile();
    } catch {
      // The payment page reads its own status and does not depend on the profile.
    }
    clearSignup();
    navigate('/signup/payment', { replace: true });
  }, [email, firstName, lastName, sport, endWith, refreshProfile, navigate]);

  const handleVerified = useCallback(async () => {
    // Single-flight: the button's disabled state alone does not cover the automatic check below.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setError('');
    setNotice('');
    setChecking(true);

    try {
      let verified: boolean;
      try {
        verified = await refreshEmailVerified();
      } catch {
        if (!hasFirebaseSession()) {
          setSignedIn(false);
          return;
        }
        setError('We could not check your verification status. Please try again.');
        return;
      }

      if (!verified) {
        setError(notVerifiedMessage(email));
        return;
      }

      await createProgram();
    } finally {
      inFlightRef.current = false;
      setChecking(false);
    }
  }, [email, createProgram]);

  // A resumed signup whose address was verified earlier has nothing to wait for.
  useEffect(() => {
    if (alreadyVerified) {
      void handleVerified();
    }
    // Mount only; the in-flight ref keeps a double-invoked effect from posting twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async () => {
    setError('');
    setNotice('');
    setResending(true);

    try {
      await sendSignupVerificationEmail();
      setNotice(`We sent a new verification link to ${email}.`);
      cooldown.start(RESEND_COOLDOWN_SECONDS);
    } catch (resendError: unknown) {
      const code =
        typeof resendError === 'object' && resendError !== null && 'code' in resendError
          ? (resendError as { code: unknown }).code
          : undefined;
      setError(
        code === 'auth/too-many-requests'
          ? 'Too many attempts, try again later.'
          : 'We could not send the verification email. Please try again.'
      );
      cooldown.start(RESEND_COOLDOWN_SECONDS);
    } finally {
      setResending(false);
    }
  };

  if (!signedIn) {
    return (
      <>
        {/* Keyed so the swap is a fresh mount and the heading takes focus. */}
        <SignupHeader key="signed-out" title="You were signed out" subtitle={email} />
        <div
          className="accept-invite-message accept-invite-message--warning"
          role="alert"
          data-testid="signup-verify-signed-out"
        >
          <p>You were signed out — sign in to continue.</p>
          <p>Use the password you chose for this account. Your details are filled in for you.</p>
        </div>
        <div className="accept-invite-actions">
          <Button
            type="button"
            fullWidth
            onClick={onSignInAgain}
            data-testid="signup-sign-in-again"
          >
            Sign in to continue
          </Button>
          <button
            type="button"
            className="signup-text-button"
            onClick={onStartOver}
            data-testid="signup-start-over"
          >
            Start over with a different email
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <SignupHeader
        key="verify"
        title="Verify your email"
        subtitle={`Check ${email} for a verification link.`}
      />

      <div className="accept-invite-message accept-invite-message--success">
        <p>Click the link in that email, then come back here and choose I&apos;ve verified.</p>
      </div>

      {error && (
        <div
          className="accept-invite-message accept-invite-message--error"
          role="alert"
          data-testid="signup-verify-error"
        >
          <p>{error}</p>
        </div>
      )}

      {notice && !error && (
        <p className="signup-status" role="status" data-testid="signup-verify-notice">
          {notice}
        </p>
      )}

      <div className="accept-invite-actions">
        <Button
          type="button"
          fullWidth
          loading={checking}
          onClick={() => void handleVerified()}
          data-testid="signup-verified-button"
        >
          I&apos;ve verified
        </Button>
        <Button
          type="button"
          variant="secondary"
          fullWidth
          loading={resending}
          disabled={checking || cooldown.remaining > 0}
          onClick={() => void handleResend()}
          data-testid="signup-resend-button"
        >
          {cooldown.remaining > 0 ? `Resend email (${cooldown.remaining}s)` : 'Resend email'}
        </Button>
        <button
          type="button"
          className="signup-text-button"
          onClick={onStartOver}
          disabled={checking}
          data-testid="signup-start-over"
        >
          Start over with a different email
        </button>
      </div>
    </>
  );
};
